import { useState, useEffect, createContext, useContext } from "react";
import { api } from "./api";

import { clearDashboardCache } from "../pages/Dashboard";

const AuthCtx = createContext(null);

export const useAuth = () => useContext(AuthCtx);

const ROLE_LEVELS = { viewer: 1, editor: 2, owner: 3 };

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    setupComplete: false,
    authed: false,
    user: null,
    account: null,
  });

  const refresh = async () => {
    try {
      const r = await api.get("/auth/status");
      setState({
        loading: false,
        setupComplete: r.data.setup_complete,
        authed: r.data.authenticated,
        user: r.data.user,
        account: r.data.account || null,
      });
    } catch {
      setState({ loading: false, setupComplete: false, authed: false, user: null, account: null });
    }
  };

  useEffect(() => { refresh(); }, []);

  const clearStaleEntity = () => {
    try {
      localStorage.removeItem("lapen_active_entity");
    } catch {}
    try {
      clearDashboardCache();
    } catch {}
  };

  const setup = async (username, password, email) => {
    clearStaleEntity();
    await api.post("/auth/setup", { username, password, email: email || null });
    await refresh();
  };
  const signup = async ({ portfolioName, username, email, password, seedDemo }) => {
    clearStaleEntity();
    await api.post("/auth/signup", {
      portfolio_name: portfolioName,
      username,
      email: email || null,
      password,
      seed_demo: !!seedDemo,
    });
    await refresh();
  };
  const login = async (username, password) => {
    clearStaleEntity();
    await api.post("/auth/login", { username, password });
    await refresh();
  };
  const logout = async () => {
    clearStaleEntity();
    await api.post("/auth/logout");
    await refresh();
  };

  const updateProfile = async ({ username, email, companyName }) => {
    const payload = {};
    if (username !== undefined) payload.username = username;
    if (email !== undefined) payload.email = email;
    if (companyName !== undefined) payload.company_name = companyName;
    const res = await api.patch("/auth/profile", payload);
    await refresh();
    return res.data;
  };

  const changePassword = async ({ currentPassword, newPassword }) => {
    const res = await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return res.data;
  };

  const revokeOtherSessions = async () => {
    const res = await api.post("/auth/revoke-other-sessions");
    return res.data;
  };

  // Permission helpers
  const role = state.user?.role || null;
  const can = {
    edit: role && ROLE_LEVELS[role] >= ROLE_LEVELS.editor,
    delete: role === "owner",
    admin: role === "owner",
    export: role === "owner" || role === "editor",
  };

  return (
    <AuthCtx.Provider
      value={{
        ...state,
        role,
        can,
        setup,
        signup,
        login,
        logout,
        refresh,
        updateProfile,
        changePassword,
        revokeOtherSessions,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
